/**
 * Public hero (landing) — synced from Project Hub `hub_site_settings.hero`,
 * exposed via RPC `get_public_site_hero()` when `published_for_public_site` is true.
 */

export const HERO_TEXT_ANIMATIONS = /** @type {const} */ ([
  ['fade-up', 'Fade up'],
  ['fade', 'Cross-fade'],
  ['slide-x', 'Slide horizontal'],
  ['scale', 'Scale in'],
  ['none', 'None'],
])

export const HERO_BG_ANIMATIONS = /** @type {const} */ ([
  ['none', 'None'],
  ['ken-burns', 'Ken Burns (slow zoom)'],
  ['slow-zoom', 'Gentle zoom'],
  ['drift', 'Drift'],
])

export const HERO_OVERLAYS = /** @type {const} */ ([
  ['none', 'None'],
  ['dark', 'Dark wash'],
  ['light', 'Light wash'],
  ['inverted', 'Inverted / film overlay'],
])

export const HERO_TEXT_EFFECTS = /** @type {const} */ ([
  ['none', 'None'],
  ['shadow-soft', 'Soft shadow'],
  ['shadow-strong', 'Strong shadow'],
  ['glow-soft', 'Soft glow'],
  ['invert-text', 'High-contrast (invert-friendly)'],
])

/** @returns {{ line1: string, line2: string }} */
function normalizePhrase(raw) {
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    const nl = trimmed.indexOf('\n')
    if (nl >= 0) {
      return {
        line1: trimmed.slice(0, nl).trim(),
        line2: trimmed.slice(nl + 1).trim(),
      }
    }
    const mid = Math.ceil(trimmed.length / 2)
    return { line1: trimmed.slice(0, mid).trim(), line2: trimmed.slice(mid).trim() || '' }
  }
  return {
    line1: String(raw?.line1 ?? '').trim(),
    line2: String(raw?.line2 ?? '').trim(),
  }
}

const DEFAULT_PHRASES = [{ line1: 'Designing purposeful solutions', line2: 'for a better future' }]

export const DEFAULT_HERO_CONFIG = {
  phrases: DEFAULT_PHRASES,
  rotate_seconds: 8,
  shuffle: true,
  text_animation: 'fade-up',
  background_animation: 'none',
  background_image_url: null,
  overlay: 'none',
  text_effect: 'none',
}

/**
 * @param {unknown} rpcData
 * @returns {typeof DEFAULT_HERO_CONFIG}
 */
export function mergeHeroFromRpc(rpcData) {
  const d = rpcData && typeof rpcData === 'object' ? rpcData : {}
  /** Explicit `phrases` (including []) means “user config from hub”. Missing key = fall back for anon / legacy. */
  const hasPhrasesProp = rpcData !== null && typeof rpcData === 'object' && 'phrases' in rpcData && Array.isArray(rpcData.phrases)

  let phrases
  if (hasPhrasesProp) {
    phrases = rpcData.phrases.map(normalizePhrase).filter((p) => p.line1 || p.line2)
  } else {
    phrases = [...DEFAULT_PHRASES]
  }

  const rotate = Number(d.rotate_seconds)
  const rotate_seconds = Number.isFinite(rotate) ? Math.min(120, Math.max(3, rotate)) : DEFAULT_HERO_CONFIG.rotate_seconds

  const bgUrl = typeof d.background_image_url === 'string' ? d.background_image_url.trim() : ''
  const background_image_url = bgUrl.startsWith('https://') ? bgUrl : null

  const text_animation = HERO_TEXT_ANIMATIONS.some(([v]) => v === d.text_animation) ? d.text_animation : DEFAULT_HERO_CONFIG.text_animation
  const background_animation = HERO_BG_ANIMATIONS.some(([v]) => v === d.background_animation)
    ? d.background_animation
    : DEFAULT_HERO_CONFIG.background_animation
  const overlay = HERO_OVERLAYS.some(([v]) => v === d.overlay) ? d.overlay : DEFAULT_HERO_CONFIG.overlay
  const text_effect = HERO_TEXT_EFFECTS.some(([v]) => v === d.text_effect) ? d.text_effect : DEFAULT_HERO_CONFIG.text_effect

  return {
    phrases,
    rotate_seconds,
    shuffle: typeof d.shuffle === 'boolean' ? d.shuffle : DEFAULT_HERO_CONFIG.shuffle,
    text_animation,
    background_animation,
    background_image_url,
    overlay,
    text_effect,
  }
}

/**
 * @param {{
 *   phrases: { line1: string, line2?: string }[],
 *   rotate_seconds: number,
 *   shuffle: boolean,
 *   text_animation: string,
 *   background_animation: string,
 *   background_image_url: string | null,
 *   overlay: string,
 *   text_effect: string,
 * }} form
 */
export function heroFormToJson(form) {
  const phrases = (form.phrases || [])
    .map((p) => ({
      line1: String(p?.line1 ?? '').trim(),
      line2: String(p?.line2 ?? '').trim(),
    }))
    .filter((p) => p.line1 || p.line2)

  const url = String(form.background_image_url ?? '').trim()
  const background_image_url = url && url.startsWith('https://') ? url : null

  return {
    phrases,
    rotate_seconds: Math.min(120, Math.max(3, Number(form.rotate_seconds) || DEFAULT_HERO_CONFIG.rotate_seconds)),
    shuffle: Boolean(form.shuffle),
    text_animation: HERO_TEXT_ANIMATIONS.some(([v]) => v === form.text_animation)
      ? form.text_animation
      : DEFAULT_HERO_CONFIG.text_animation,
    background_animation: HERO_BG_ANIMATIONS.some(([v]) => v === form.background_animation)
      ? form.background_animation
      : DEFAULT_HERO_CONFIG.background_animation,
    background_image_url,
    overlay: HERO_OVERLAYS.some(([v]) => v === form.overlay) ? form.overlay : DEFAULT_HERO_CONFIG.overlay,
    text_effect: HERO_TEXT_EFFECTS.some(([v]) => v === form.text_effect)
      ? form.text_effect
      : DEFAULT_HERO_CONFIG.text_effect,
  }
}
